import { useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { motion } from "framer-motion";
import { Helmet } from "react-helmet";
import useCanonical from "@/hooks/useCanonical";
import useMetaDescription from "@/hooks/useMetaDescription";
import usePageSEOOverride from "@/hooks/useSEOOverrides";
import {
  ArrowRight, CheckCircle, Building2, Users, Award, Phone, MapPin,
  Factory, Truck, Wrench, Shield, Star, Layers, Monitor, Armchair,
  LayoutGrid, DoorOpen, Archive, ChevronRight, Clock, Headphones,
  Rocket, Landmark, Briefcase, Globe, PlayCircle
} from "lucide-react";
import { Button } from "@/components/ui/button";

// Anchor helper — smooth-scrolls to a target section id, offsetting for sticky nav.
function scrollToId(id) {
  const el = document.getElementById(id);
  if (!el) return;
  const y = el.getBoundingClientRect().top + window.scrollY - 90;
  window.scrollTo({ top: y, behavior: "smooth" });
}

// Product tile — kept at module scope so the visual-edits plugin can trace props cleanly.
function ProductTile({ image, title, desc, cta, href, isAnchor, anchorId, testId }) {
  // Only build <picture> WebP fallback for LOCAL raster images. Unsplash handles
  // format negotiation on its own (via ?auto=format), and a fake .webp URL there
  // would 404 the source and break the picture fallback.
  const isLocal = typeof image === "string" && image.startsWith("/") && /\.(png|jpe?g)$/i.test(image);
  const webp = isLocal ? image.replace(/\.(png|jpe?g)$/i, ".webp") : null;
  const inner = (
    <div className="group bg-white border border-[#E2E8F0] rounded-lg overflow-hidden hover:shadow-xl hover:-translate-y-1 transition-all duration-300 h-full flex flex-col" data-testid={testId}>
      <div className="aspect-[4/3] overflow-hidden bg-[#F1F5F9]">
        {webp ? (
          <picture>
            <source srcSet={webp} type="image/webp" />
            <img
              src={image}
              alt={title}
              loading="lazy"
              decoding="async"
              className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
            />
          </picture>
        ) : (
          <img
            src={image}
            alt={title}
            loading="lazy"
            decoding="async"
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
          />
        )}
      </div>
      <div className="p-5 flex flex-col flex-1">
        <h3 className="text-lg font-bold text-[#0F172A] mb-2">{title}</h3>
        <p className="text-sm text-[#64748B] mb-4 flex-1 leading-relaxed">{desc}</p>
        <span className="text-sm font-semibold text-[#F97316] group-hover:underline inline-flex items-center gap-1">
          {cta} <ArrowRight className="w-4 h-4" />
        </span>
      </div>
    </div>
  );
  if (isAnchor) {
    return (
      <button
        type="button"
        onClick={() => scrollToId(anchorId)}
        className="text-left block w-full"
        aria-label={`Jump to ${title} section`}
      >
        {inner}
      </button>
    );
  }
  return <Link to={href} className="block h-full">{inner}</Link>;
}

const ALL_PRODUCTS = [
  { name: "Linear Workstations", slug: "linear-workstations", desc: "Open-plan and modular workstation systems for IT parks, BPOs, and corporate offices. Designed for high-density seating with integrated cable management.", icon: Monitor },
  { name: "Acoustic Workstations", slug: "acoustic-workstations", desc: "Noise-reducing workstation systems with acoustic panels for call centres, BPOs, and focused work environments.", icon: Monitor },
  { name: "Modular Desks", slug: "modular-desks", desc: "Flexible desk systems for startups and agile offices. Easy to reconfigure as teams grow.", icon: Monitor },
  { name: "Executive Desks", slug: "executive-desks", desc: "Premium desks for corner offices and senior management with refined finishes.", icon: Monitor },
  { name: "Public Sector Desks", slug: "public-sector-desks", desc: "Heavy-duty desks built for public sector offices.", icon: Monitor },
  { name: "Executive Chairs", slug: "executive-chairs", desc: "High-back task chairs with premium upholstery, lumbar support, and synchronised tilt mechanisms.", icon: Armchair },
  { name: "24/7 Chairs", slug: "24-7-chairs", desc: "Heavy-duty chairs rated for round-the-clock use in call centres, data centres, and BPO facilities.", icon: Armchair },
  { name: "Visitor Chairs", slug: "visitor-chairs", desc: "Professional seating for reception areas, waiting rooms, and client-facing conference spaces.", icon: Armchair },
  { name: "Customer Seating", slug: "customer-seating", desc: "Front-office seating solutions for banks, insurance offices, and customer service centres.", icon: Armchair },
  { name: "Conference Tables", slug: "conference-tables", desc: "Boardroom-grade tables with integrated power, data connectivity, and premium surface finishes.", icon: LayoutGrid },
  { name: "Boardroom Tables", slug: "boardroom-tables", desc: "Large-format tables for C-suite boardrooms and executive meeting spaces.", icon: LayoutGrid },
  { name: "Meeting Tables", slug: "meeting-tables", desc: "Compact discussion tables for team huddles, project meetings, and collaboration zones.", icon: LayoutGrid },
  { name: "Height Adjustable Desks", slug: "height-adjustable-desks", desc: "Electric sit-stand desks with dual motors and programmable memory for flexible working positions.", icon: Monitor },
  { name: "Standing Desks", slug: "standing-desks", desc: "Manual and electric standing desk converters for flexible work environments.", icon: Monitor },
  { name: "Glass Partitions", slug: "glass-partitions", desc: "Single glazed, double glazed, and grid framed partition systems for executive cabins and open offices.", icon: Layers },
  { name: "Phone Booths", slug: "phone-booths", desc: "Acoustic phone booths for private calls and focused work in open-plan offices.", icon: DoorOpen },
  { name: "Meeting Pods", slug: "meeting-pods", desc: "Enclosed collaboration pods for 2-6 people with acoustic insulation and ventilation.", icon: DoorOpen },
  { name: "Meeting Booths", slug: "meeting-booths", desc: "Semi-enclosed booth seating for informal meetings and brainstorming sessions.", icon: DoorOpen },
  { name: "Filing Systems", slug: "filing-systems", desc: "Compactor filing, lateral filing, and vertical filing solutions for organized document storage.", icon: Archive },
  { name: "Locker Systems", slug: "locker-systems", desc: "Personal and shared locker systems for agile, hot-desking, and co-working environments.", icon: Archive },
  { name: "Filing Cabinets", slug: "filing-cabinets", desc: "Standard and fire-resistant filing cabinets for corporate and public sector offices.", icon: Archive },
  { name: "Storage Units", slug: "storage-units", desc: "Pedestals, overhead cabinets, and mobile storage for workstation integration.", icon: Archive },
  { name: "Secure Cabinets", slug: "secure-cabinets", desc: "High-security storage for banking, BFSI, and data-sensitive environments.", icon: Archive },
  { name: "Safe Storage", slug: "safe-storage", desc: "Vault-grade safe storage solutions for valuables and confidential documents.", icon: Archive },
];

const ALL_INDUSTRIES = [
  { name: "IT & Technology", slug: "it-technology", desc: "Agile workstations and collaboration zones for tech companies" },
  { name: "Corporate Headquarters", slug: "corporate-hq", desc: "Premium furniture for executive offices and brand headquarters" },
  { name: "Co-working Spaces", slug: "coworking", desc: "Flexible, durable furniture for shared workspace operators" },
  { name: "BPO & Call Centres", slug: "bpo", desc: "Acoustic solutions and 24/7 rated seating for operations" },
  { name: "Startups", slug: "startups", desc: "Budget-friendly modular setups that scale with your team" },
  { name: "Banking & BFSI", slug: "banking-finance", desc: "Secure workspaces and customer service counters" },
  { name: "Office Partitions", slug: "office-partitions", desc: "Glass and modular partition systems for space division" },
  { name: "Global Capability Centres", slug: "gcc", desc: "International-standard furniture for GCCs of large MNCs" },
  { name: "FinTech Companies", slug: "fintech", desc: "Modern, tech-forward furniture for financial innovation hubs" },
  { name: "Logistics & Warehousing", slug: "logistics", desc: "Durable warehouse-office integration furniture" },
  { name: "Pharma Corporate", slug: "pharma", desc: "Clean, compliant furniture for pharmaceutical offices" },
  { name: "Data Centres", slug: "data-centers", desc: "NOC desks and 24/7 operator workstations" },
];

const SUPPORT_PAGES = [
  { title: "Modular Office Workstations in Mumbai", url: "/modular-office-workstations-mumbai", desc: "Linear, cluster, and acoustic workstation systems for IT parks and corporate offices." },
  { title: "Office Partitions & Cubicles", url: "/office-partitions-and-cubicles", desc: "Glass partitions, modular walls, and cubicle systems for workspace privacy." },
  { title: "Corporate Office Furniture Solutions", url: "/corporate-office-furniture-solutions", desc: "End-to-end furniture solutions for corporate headquarters and enterprise offices." },
  { title: "Office Furniture Manufacturer in Mumbai", url: "/office-furniture-manufacturer-mumbai", desc: "Own manufacturing facility in Bhiwandi, Thane with automated machinery and ISO 9001:2015 certification." },
];

const CITY_PAGES = [
  { name: "Navi Mumbai", url: "/modular-office-workstations-navi-mumbai" },
  { name: "Thane", url: "/corporate-office-furniture-thane" },
  { name: "Pune", url: "/office-furniture-solutions-pune" },
  { name: "Bangalore", url: "/corporate-office-furniture-bangalore" },
  { name: "Delhi NCR", url: "/office-furniture-solutions-delhi-ncr" },
  { name: "Hyderabad", url: "/modular-office-furniture-hyderabad" },
  { name: "Chennai", url: "/office-furniture-chennai" },
  { name: "Ahmedabad", url: "/office-furniture-ahmedabad" },
];

export default function PowerPage() {
  const location = useLocation();
  // Use the ACTUAL URL displayed in the browser (which may be an alias URL for SEO renames).
  // usePageSEOOverride fetches Speicher's portal-configured meta/canonical/schema for whichever
  // URL is rendered. Falls back to the hardcoded defaults below when no override exists.
  const currentPath = location.pathname;
  usePageSEOOverride(currentPath, {
    title: "Office Furniture Manufacturer in Mumbai | Modular Workstations & Interiors Since 2014 | Ezeefit",
    description: "Ezeefit — modular office furniture manufacturer in Mumbai since 2014. Workstations, chairs, conference tables, partitions, storage. ISO 9001:2015. Bhiwandi factory.",
    canonical: `https://ezeefitofficesystems.in${currentPath}`,
  });

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const localBusinessSchema = {
    "@context": "https://schema.org",
    "@type": "LocalBusiness",
    "name": "Ezeefit Office Systems",
    "description": "a Mumbai-based modular office furniture manufacturer since 2014. Complete range of workstations, chairs, tables, partitions, and storage solutions.",
    "url": "https://ezeefitofficesystems.in/office-furniture-mumbai",
    "telephone": "+91-9820042100",
    "address": { "@type": "PostalAddress", "streetAddress": "6th Floor, A/611-612, Crystal Plaza Premises, New Link Road, Opp. Infinity Mall, Andheri West", "addressLocality": "Mumbai", "addressRegion": "Maharashtra", "postalCode": "400053", "addressCountry": "IN" },
    "geo": { "@type": "GeoCoordinates", "latitude": "19.1360", "longitude": "72.8344" },
    "openingHours": "Mo-Sa 09:00-18:00",
    "priceRange": "$$"
  };

  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": [
      { "@type": "Question", "name": "Who manufactures office furniture in Mumbai?", "acceptedAnswer": { "@type": "Answer", "text": "Ezeefit Office Systems is a Mumbai-based modular office furniture manufacturer established in 2014 with own factory in Bhiwandi, Thane and ISO 9001:2015 certification." }},
      { "@type": "Question", "name": "What types of office furniture does Ezeefit manufacture?", "acceptedAnswer": { "@type": "Answer", "text": "Ezeefit manufactures 24+ product categories including modular workstations, executive chairs, visitor chairs, 24/7 chairs, conference tables, boardroom tables, height adjustable desks, standing desks, phone booths, meeting pods, glass partitions, filing systems, locker systems, and secure storage." }},
      { "@type": "Question", "name": "What is the delivery time for office furniture in Mumbai?", "acceptedAnswer": { "@type": "Answer", "text": "Standard orders deliver within 3-6 weeks. Large corporate projects take 6-12 weeks including design, manufacturing, delivery, and professional installation." }},
      { "@type": "Question", "name": "Do you deliver office furniture outside Mumbai?", "acceptedAnswer": { "@type": "Answer", "text": "Yes, Ezeefit delivers and installs across India including Navi Mumbai, Thane, Pune, Bangalore, Delhi NCR, Hyderabad, Chennai, and Ahmedabad." }},
      { "@type": "Question", "name": "Can office furniture be customized?", "acceptedAnswer": { "@type": "Answer", "text": "Absolutely. We offer full customization — dimensions, finishes, colours, materials, and configurations. Our design team creates tailored solutions matching your workspace and brand requirements." }},
      { "@type": "Question", "name": "Is Ezeefit a direct manufacturer or dealer?", "acceptedAnswer": { "@type": "Answer", "text": "Ezeefit is a direct manufacturer with own factory in Bhiwandi, Thane. We design, manufacture, and install — eliminating middlemen and ensuring factory-direct pricing and quality control." }}
    ]
  };

  return (
    <>
      {/* Schema.org JSON-LD - managed by React DOM for proper cleanup */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(localBusinessSchema) }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(faqSchema) }}
      />
    <div className="pt-20" data-testid="power-page">
      {/* Meta tags (description, canonical, og:*) are handled dynamically by
          usePageSEOOverride based on the current URL. This lets Speicher configure
          meta title / description / canonical / og:* for the aliased URL via the SEO
          portal and have them apply on the live page. Keeping og:type + keywords
          static here as those are not editor-overridable. */}
      <Helmet>
        <meta name="keywords" content="office furniture Mumbai, modular office furniture Mumbai, office furniture manufacturer Mumbai, buy office furniture Mumbai, office workstation Mumbai, office chair Mumbai, conference table Mumbai, office partition Mumbai, corporate office furniture Mumbai" />
        <meta property="og:type" content="website" />
      </Helmet>

      {/* Hero */}
      <section className="relative bg-[#0F172A] py-12 md:py-20 lg:py-28 overflow-hidden">
        <div className="absolute inset-0 opacity-5" style={{ backgroundImage: 'url("data:image/svg+xml,%3Csvg width=\'40\' height=\'40\' viewBox=\'0 0 40 40\' xmlns=\'http://www.w3.org/2000/svg\'%3E%3Cpath d=\'M20 20h20v20H20z\' fill=\'%23fff\' fill-opacity=\'.05\'/%3E%3C/svg%3E")' }} />
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24 relative z-10">
          {/* 3-cell grid: on mobile it flows Header → Collage → Body.
              On desktop the collage occupies the right column across both rows. */}
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 lg:gap-10 items-start">
            {/* Cell 1: Header (kicker + H1) — always at top */}
            <motion.div
              className="lg:col-span-7 lg:row-start-1"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
            >
              <span className="font-mono text-xs sm:text-sm text-[#F97316] tracking-wider uppercase mb-3 block">Office Furniture Manufacturer in Mumbai</span>
              <h1 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold text-white leading-tight">
                Office Furniture in Mumbai —<br />Manufactured, Delivered & Installed
              </h1>
            </motion.div>

            {/* Cell 2: Collage — sits between Header and Body on mobile; anchors to right column across both rows on desktop */}
            <motion.div
              className="lg:col-span-5 lg:col-start-8 lg:row-start-1 lg:row-span-2 flex justify-center lg:justify-end lg:self-center"
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.7, delay: 0.15 }}
              data-testid="hero-collage"
            >
              <div className="relative w-[260px] h-[260px] xs:w-[280px] xs:h-[280px] sm:w-[360px] sm:h-[360px] lg:w-[440px] lg:h-[440px]">
                {/* Soft orange glow behind the collage */}
                <div className="absolute inset-6 rounded-full bg-gradient-to-br from-[#F97316]/25 via-transparent to-transparent blur-2xl" aria-hidden="true" />

                {/* Circle 1 — Workstations (top-left, largest) */}
                <button
                  type="button"
                  onClick={() => scrollToId("workstations")}
                  aria-label="See workstations"
                  className="absolute top-0 left-0 w-[62%] h-[62%] rounded-full overflow-hidden ring-4 ring-white/10 shadow-2xl hover:ring-[#F97316]/70 hover:scale-[1.03] transition-all duration-500 group"
                  data-testid="hero-collage-workstations"
                >
                  <picture>
                    <source srcSet="/media/landing/workstations.webp" type="image/webp" />
                    <img
                      src="/media/landing/workstations.jpg"
                      alt="Modular office workstations manufactured in Mumbai"
                      loading="eager"
                      decoding="async"
                      className="w-full h-full object-cover"
                    />
                  </picture>
                  <span className="absolute bottom-3 left-0 right-0 text-center text-[10px] sm:text-xs font-semibold uppercase tracking-wider text-white opacity-0 group-hover:opacity-100 transition-opacity">
                    Workstations
                  </span>
                </button>

                {/* Circle 2 — Phone Booths (top-right, smaller) */}
                <button
                  type="button"
                  onClick={() => scrollToId("phone-booths")}
                  aria-label="See acoustic phone booths"
                  className="absolute top-[6%] right-0 w-[46%] h-[46%] rounded-full overflow-hidden ring-4 ring-white/10 shadow-2xl hover:ring-[#F97316]/70 hover:scale-[1.03] transition-all duration-500 group"
                  data-testid="hero-collage-phonebooths"
                >
                  <picture>
                    <source srcSet="/media/landing/phone-booths.webp" type="image/webp" />
                    <img
                      src="/media/landing/phone-booths.jpg"
                      alt="Acoustic phone booths for open-plan offices"
                      loading="eager"
                      decoding="async"
                      className="w-full h-full object-cover"
                    />
                  </picture>
                  <span className="absolute bottom-2 left-0 right-0 text-center text-[10px] font-semibold uppercase tracking-wider text-white opacity-0 group-hover:opacity-100 transition-opacity">
                    Phone Booths
                  </span>
                </button>

                {/* Circle 3 — Conference Tables (bottom-center, medium) */}
                <button
                  type="button"
                  onClick={() => scrollToId("conference-tables")}
                  aria-label="See conference tables"
                  className="absolute bottom-0 left-[18%] w-[58%] h-[58%] rounded-full overflow-hidden ring-4 ring-white/10 shadow-2xl hover:ring-[#F97316]/70 hover:scale-[1.03] transition-all duration-500 group"
                  data-testid="hero-collage-conference"
                >
                  <picture>
                    <source srcSet="/media/landing/conference-tables.webp" type="image/webp" />
                    <img
                      src="/media/landing/conference-tables.jpg"
                      alt="Boardroom conference tables with cable management"
                      loading="eager"
                      decoding="async"
                      className="w-full h-full object-cover"
                    />
                  </picture>
                  <span className="absolute bottom-3 left-0 right-0 text-center text-[10px] sm:text-xs font-semibold uppercase tracking-wider text-white opacity-0 group-hover:opacity-100 transition-opacity">
                    Conference Tables
                  </span>
                </button>

                {/* "Since 2014" orange badge — visible on all breakpoints */}
                <div className="absolute top-[36%] right-[4%] sm:right-[6%] z-10 flex flex-col items-center justify-center w-14 h-14 sm:w-20 sm:h-20 rounded-full bg-[#F97316] text-white shadow-xl ring-4 ring-[#0F172A]">
                  <span className="text-[9px] sm:text-xs font-mono uppercase leading-none tracking-wider">Since</span>
                  <span className="text-base sm:text-xl font-bold leading-none mt-1">2014</span>
                </div>
              </div>
            </motion.div>

            {/* Cell 3: Body (paragraphs, CTAs, anchor bar) — sits below collage on mobile, below H1 on desktop */}
            <motion.div
              className="lg:col-span-7 lg:col-start-1 lg:row-start-2"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.1 }}
            >
              <p className="text-sm sm:text-base md:text-lg text-[#94A3B8] max-w-3xl mb-4 leading-relaxed">
                <strong className="text-white">Ezeefit Office Systems</strong> is a Mumbai-based <strong className="text-white">modular office furniture manufacturer since 2014</strong>. From our <Link to="/manufacturing" className="text-[#F97316] underline hover:text-orange-400">factory in Bhiwandi, Thane</Link>, we design, manufacture, and install complete office interiors — <Link to="/products/linear-workstations" className="text-[#F97316] underline hover:text-orange-400">workstations</Link>, <Link to="/products/executive-chairs" className="text-[#F97316] underline hover:text-orange-400">task chairs</Link>, <Link to="/products/conference-tables" className="text-[#F97316] underline hover:text-orange-400">conference tables</Link>, <Link to="/products/glass-partitions" className="text-[#F97316] underline hover:text-orange-400">glass partitions</Link>, and <Link to="/products/filing-systems" className="text-[#F97316] underline hover:text-orange-400">storage systems</Link>.
              </p>
              <p className="text-sm sm:text-base text-[#94A3B8] max-w-3xl mb-6 leading-relaxed">
                With <strong className="text-white">ISO 9001:2015-certified quality</strong> and a <Link to="/process" className="text-[#F97316] underline hover:text-orange-400">9-step execution process</Link>, we furnish offices for IT companies, banks, public sector institutions, co-working spaces, startups, and MNCs across India.
              </p>
              <div className="flex flex-wrap gap-3 sm:gap-4">
                <Link to="/contact">
                  <Button className="bg-[#F97316] hover:bg-[#EA580C] text-white px-6 sm:px-8 py-5 sm:py-6 text-sm sm:text-base font-semibold" data-testid="power-cta-consultation">
                    Get Free Consultation <ArrowRight className="w-4 h-4 sm:w-5 sm:h-5 ml-2" />
                  </Button>
                </Link>
                <Link to="/manufacturing">
                  <Button variant="outline" className="border-white/30 text-white hover:bg-white/10 px-6 sm:px-8 py-5 sm:py-6 text-sm sm:text-base" data-testid="power-cta-factory">
                    Visit Our Factory
                  </Button>
                </Link>
              </div>

              {/* Quick anchor jumps — improves dwell time + Google Ads Landing Page Experience score */}
              <div className="mt-6 sm:mt-8 flex flex-wrap items-center gap-2 sm:gap-3" data-testid="power-anchor-bar">
                <span className="text-xs sm:text-sm font-semibold uppercase tracking-wider text-[#F97316] mr-1">Jump to:</span>
                <button
                  type="button"
                  onClick={() => scrollToId("workstations")}
                  className="inline-flex items-center gap-1.5 px-3 sm:px-4 py-2 rounded-full border border-white/30 text-white text-xs sm:text-sm hover:bg-white/10 hover:border-white/60 transition-colors"
                  data-testid="anchor-workstations"
                >
                  <Layers className="w-3.5 h-3.5" /> Workstations
                </button>
                <button
                  type="button"
                  onClick={() => scrollToId("phone-booths")}
                  className="inline-flex items-center gap-1.5 px-3 sm:px-4 py-2 rounded-full border border-white/30 text-white text-xs sm:text-sm hover:bg-white/10 hover:border-white/60 transition-colors"
                  data-testid="anchor-phone-booths"
                >
                  <Headphones className="w-3.5 h-3.5" /> Phone Booths
                </button>
                <button
                  type="button"
                  onClick={() => scrollToId("conference-tables")}
                  className="inline-flex items-center gap-1.5 px-3 sm:px-4 py-2 rounded-full border border-white/30 text-white text-xs sm:text-sm hover:bg-white/10 hover:border-white/60 transition-colors"
                  data-testid="anchor-conference-tables"
                >
                  <LayoutGrid className="w-3.5 h-3.5" /> Conference Tables
                </button>
                <button
                  type="button"
                  onClick={() => scrollToId("factory-video")}
                  className="inline-flex items-center gap-1.5 px-3 sm:px-4 py-2 rounded-full border border-white/30 text-white text-xs sm:text-sm hover:bg-white/10 hover:border-white/60 transition-colors"
                  data-testid="anchor-factory-video"
                >
                  <PlayCircle className="w-3.5 h-3.5" /> Factory Video
                </button>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Product Gallery — above-the-fold visual proof for Google Ads landing-page experience.
          4 tiles link to core product pages + factory-video anchor. All images are locally-served WebP for speed. */}
      <section className="py-14 md:py-16 bg-white border-b border-[#E2E8F0]">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <div className="text-center mb-10">
            <span className="font-mono text-xs sm:text-sm text-[#F97316] tracking-wider uppercase mb-2 block">Explore Our Range</span>
            <h2 className="text-2xl md:text-3xl font-bold text-[#0F172A]">Modular Office Furniture Made in Mumbai</h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 md:gap-6">
            <div id="workstations">
              <ProductTile
                image="/media/landing/workstations.jpg"
                title="Workstations"
                desc="Modular linear and cluster workstations for open-plan offices. Cable management, ergonomic build, custom finishes."
                cta="Explore workstations"
                href="/products/linear-workstations"
                testId="tile-workstations"
              />
            </div>
            <div id="phone-booths">
              <ProductTile
                image="/media/landing/phone-booths.jpg"
                title="Acoustic Phone Booths"
                desc="Private call and focus booths with acoustic panels. Ventilated, single and 2-person configurations, plug-and-play install."
                cta="Explore booths"
                href="/products/phone-booths"
                testId="tile-phone-booths"
              />
            </div>
            <div id="conference-tables">
              <ProductTile
                image="/media/landing/conference-tables.jpg"
                title="Conference Tables"
                desc="6, 8 and 10-seat meeting-room tables with cable ports and matching credenzas. Custom sizes on request."
                cta="Explore conference tables"
                href="/products/conference-tables"
                testId="tile-conference-tables"
              />
            </div>
            <div id="factory-video">
              <ProductTile
                image="/media/landing/factory-tour.jpg"
                title="Factory Tour"
                desc="60-second walkthrough of our Bhiwandi factory — automated edge banding, CNC borers, panel saws, laminate press."
                cta="Watch the tour"
                href="/manufacturing"
                testId="tile-factory-video"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Trust Metrics */}
      <section className="bg-white py-12 border-b">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <div className="grid grid-cols-2 md:grid-cols-5 gap-8 text-center">
            {[
              { num: "2014", label: "Established" },
              { num: "ISO 9001:2015", label: "Certified" },
              { num: "Mumbai", label: "Own Factory" },
              { num: "24+", label: "Product Categories" },
              { num: "13+", label: "Industry Solutions" },
            ].map((s, i) => (
              <div key={i}>
                <div className="text-3xl md:text-4xl font-bold text-[#F97316]">{s.num}</div>
                <div className="text-sm text-[#64748B] mt-1">{s.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Why Ezeefit - Expanded */}
      <section className="py-20 bg-[#F8FAFC]">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <h2 className="text-2xl md:text-3xl font-bold text-[#0F172A] mb-4">Why Companies Choose Ezeefit for Office Furniture</h2>
          <p className="text-[#64748B] max-w-4xl mb-12 leading-relaxed">
            Unlike dealers and resellers, Ezeefit is a <strong>direct manufacturer</strong>. Every piece of furniture is crafted in our <Link to="/manufacturing" className="text-[#F97316] underline">Bhiwandi factory</Link> using automated edge banding machines, CNC borers, panel saws, and laminate press equipment. This means you get <strong>factory-direct pricing</strong>, consistent quality, and full customization without middleman markups. See <Link to="/testimonials" className="text-[#F97316] underline">what our clients say</Link>.
          </p>
          <div className="grid md:grid-cols-3 gap-8">
            {[
              { icon: Factory, title: "Own Manufacturing Facility", desc: "Manufacturing facility in Bhiwandi, Thane with automated edge banding, CNC borers, panel saws, and laminate press machines. No outsourcing — everything made in-house." },
              { icon: Shield, title: "ISO 9001:2015 Certified Quality", desc: "All products meet international ISO 9001:2015 quality standards for durability and consistent build quality. Regular third-party testing ensures consistent quality across every batch." },
              { icon: Truck, title: "Pan-India Delivery & Installation", desc: "Professional delivery and installation teams across Mumbai, Navi Mumbai, Thane, Pune, Bangalore, Delhi, Hyderabad, Chennai, and Ahmedabad." },
              { icon: Wrench, title: "Full Customization", desc: "Custom dimensions, finishes, laminate colours, handle styles, and configurations. Our design team creates 3D layouts and shop drawings before manufacturing begins." },
              { icon: Clock, title: "On-Time Project Delivery", desc: "Strict timelines with our 9-step execution process — from requirement analysis to final handover. We control the entire supply chain." },
              { icon: Star, title: "Manufacturing Since 2014", desc: "Established in 2014. We design and manufacture for offices of every scale — from small startups to large corporate campuses — across every budget level." },
            ].map((item, i) => (
              <motion.div key={i} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.08 }} className="bg-white p-6 border border-gray-100">
                <item.icon className="w-8 h-8 text-[#F97316] mb-4" />
                <h3 className="font-bold text-[#0F172A] mb-2">{item.title}</h3>
                <p className="text-sm text-[#64748B] leading-relaxed">{item.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* COMPLETE Product Range - ALL 24 Categories */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <h2 className="text-2xl md:text-3xl font-bold text-[#0F172A] mb-4">Complete Office Furniture Range — 24+ Product Categories</h2>
          <p className="text-[#64748B] max-w-4xl mb-12 leading-relaxed">
            From <Link to="/products/linear-workstations" className="text-[#F97316] underline">open-plan workstations</Link> and <Link to="/products/executive-chairs" className="text-[#F97316] underline">task chairs</Link> to <Link to="/products/glass-partitions" className="text-[#F97316] underline">glass partition systems</Link> and <Link to="/products/phone-booths" className="text-[#F97316] underline">acoustic phone booths</Link> — we manufacture everything in-house. Browse our complete product range or <Link to="/contact" className="text-[#F97316] underline">request a custom quote</Link>.
          </p>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
            {ALL_PRODUCTS.map((cat, i) => (
              <Link key={i} to={`/products/${cat.slug}`} className="group block bg-[#F8FAFC] p-5 border border-gray-100 hover:border-[#F97316] transition-colors">
                <cat.icon className="w-7 h-7 text-[#F97316] mb-2" />
                <h3 className="font-bold text-[#0F172A] text-sm mb-1 group-hover:text-[#F97316] transition-colors">{cat.name}</h3>
                <p className="text-xs text-[#64748B] line-clamp-2">{cat.desc}</p>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Industries Served - ALL 13 */}
      <section className="py-20 bg-[#0F172A]">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <h2 className="text-2xl md:text-3xl font-bold text-white mb-4">Office Furniture for 13+ Industries</h2>
          <p className="text-[#94A3B8] max-w-4xl mb-12 leading-relaxed">
            Every industry has unique workspace requirements. An <Link to="/solutions/it-technology" className="text-[#F97316] underline">IT company</Link> needs agile workstation clusters. A <Link to="/solutions/bpo" className="text-[#F97316] underline">BPO</Link> needs acoustic panels and <Link to="/products/24-7-chairs" className="text-[#F97316] underline">24/7-rated chairs</Link>. A <Link to="/solutions/banking-finance" className="text-[#F97316] underline">bank</Link> needs secure cabinets and customer counters. We design and manufacture <Link to="/solutions" className="text-[#F97316] underline">industry-specific solutions</Link> for all of them.
          </p>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
            {ALL_INDUSTRIES.map((ind, i) => (
              <Link key={i} to={`/solutions/${ind.slug}`} className="group flex items-start gap-3 bg-white/5 border border-white/10 p-4 hover:border-[#F97316] transition-colors">
                <Building2 className="w-5 h-5 text-[#F97316] shrink-0 mt-0.5" />
                <div>
                  <span className="text-white text-sm font-semibold group-hover:text-[#F97316] transition-colors">{ind.name}</span>
                  <p className="text-xs text-[#94A3B8] mt-0.5">{ind.desc}</p>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* How We Deliver */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <h2 className="text-2xl md:text-3xl font-bold text-[#0F172A] mb-4">Our Proven 9-Step Office Furniture Execution Process</h2>
          <p className="text-[#64748B] max-w-4xl mb-12 leading-relaxed">
            From the first call to final handover, our <Link to="/process" className="text-[#F97316] underline">systematic execution process</Link> ensures your office furniture project in Mumbai is delivered on time, within budget, and to the highest quality standards. Every step is tracked, documented, and communicated.
          </p>
          <div className="grid md:grid-cols-3 gap-6">
            {[
              { step: "01", title: "Requirement Understanding", desc: "We study your space, headcount, workflow, and brand guidelines." },
              { step: "02", title: "Design & Space Planning", desc: "3D layouts and floor plans optimized for productivity and aesthetics." },
              { step: "03", title: "Budgeting & Quotation", desc: "Transparent, detailed pricing with no hidden costs." },
              { step: "04", title: "Shop Drawing Approvals", desc: "Precise manufacturing drawings approved before production begins." },
              { step: "05", title: "Manufacturing", desc: "CNC-precision manufacturing in our Bhiwandi factory." },
              { step: "06", title: "Quality Inspection", desc: "Every piece inspected against ISO 9001:2015 quality standards before dispatch." },
              { step: "07", title: "Dispatch & Delivery", desc: "Protected packaging and professional logistics across India." },
              { step: "08", title: "Installation & Handover", desc: "Expert installation team ensures seamless, disruption-free setup." },
              { step: "09", title: "After-Sales Support", desc: "Warranty service, maintenance, and expansion support." },
            ].map((s, i) => (
              <div key={i} className="flex items-start gap-4 p-4 bg-[#F8FAFC] border border-gray-100">
                <span className="text-[#F97316] font-mono text-lg font-bold shrink-0">{s.step}</span>
                <div>
                  <h3 className="text-[#0F172A] font-semibold text-sm">{s.title}</h3>
                  <p className="text-xs text-[#64748B] mt-1">{s.desc}</p>
                </div>
              </div>
            ))}
          </div>
          <Link to="/process" className="inline-flex items-center gap-2 text-[#F97316] font-semibold mt-8 hover:underline">
            View Detailed Process <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </section>

      {/* Topic Cluster - Supporting Pages */}
      <section className="py-20 bg-[#F8FAFC]">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <h2 className="text-2xl md:text-3xl font-bold text-[#0F172A] mb-4">Explore Our Office Furniture Expertise</h2>
          <p className="text-[#64748B] max-w-3xl mb-12">In-depth guides and solutions for specific office furniture categories and requirements.</p>
          <div className="grid md:grid-cols-2 gap-6">
            {SUPPORT_PAGES.map((page, i) => (
              <Link key={i} to={page.url} className="group bg-white p-6 border border-gray-100 hover:border-[#F97316] transition-colors flex items-start gap-4">
                <div className="w-10 h-10 bg-[#F97316]/10 flex items-center justify-center shrink-0">
                  <span className="text-[#F97316] font-bold font-mono">0{i + 1}</span>
                </div>
                <div>
                  <h3 className="font-bold text-[#0F172A] group-hover:text-[#F97316] transition-colors mb-1">{page.title}</h3>
                  <p className="text-sm text-[#64748B]">{page.desc}</p>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* We Deliver Across India */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <h2 className="text-2xl md:text-3xl font-bold text-[#0F172A] mb-4">Office Furniture Delivery Across India</h2>
          <p className="text-[#64748B] max-w-4xl mb-12 leading-relaxed">
            While our factory is in <strong>Navi Mumbai</strong>, we deliver and install office furniture across India. Our professional installation teams operate in all major cities. Click on a city to explore furniture solutions available in your location.
          </p>
          <div className="flex flex-wrap gap-3">
            {CITY_PAGES.map((city, i) => (
              <Link key={i} to={city.url} className="px-5 py-3 bg-[#0F172A] text-white text-sm font-semibold hover:bg-[#F97316] transition-colors flex items-center gap-2">
                <MapPin className="w-4 h-4" />
                {city.name}
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-20 bg-[#F8FAFC]">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <h2 className="text-2xl md:text-3xl font-bold text-[#0F172A] mb-12">Frequently Asked Questions About Office Furniture in Mumbai</h2>
          <div className="space-y-6 max-w-4xl">
            {[
              { q: "Who is the office furniture manufacturer in Mumbai?", a: "Ezeefit Office Systems is one of a Mumbai-based modular office furniture manufacturers, operating since 2014. With our own factory in Bhiwandi, Thane, automated machinery, ISO 9001:2015 certification, and corporate clients, we provide commercial-grade furniture at factory-direct prices — no middlemen, no markups." },
              { q: "What types of office furniture does Ezeefit manufacture?", a: "We manufacture 24+ product categories: linear workstations, acoustic workstations, modular desks, executive desks, public sector desks, executive chairs, 24/7 chairs, visitor chairs, customer seating, conference tables, boardroom tables, meeting tables, height adjustable desks, standing desks, glass partitions, phone booths, meeting pods, meeting booths, filing systems, locker systems, filing cabinets, storage units, secure cabinets, and safe storage solutions." },
              { q: "Do you provide office furniture for bulk corporate orders?", a: "Yes, we specialize in bulk corporate orders. Whether you need 50 workstations or 5,000, our factory handles large-scale production with consistent quality and on-time delivery. We have furnished offices for IT companies, banks, public sector institutions, startups, and MNCs across India." },
              { q: "What is the delivery timeline for office furniture in Mumbai?", a: "Standard orders in Mumbai deliver within 3-6 weeks. Large corporate projects (large-scale offices) take 6-12 weeks including design consultation, shop drawing approvals, manufacturing, delivery, and professional installation." },
              { q: "Do you deliver office furniture outside Mumbai?", a: "Yes. While our factory is in Bhiwandi, Thane, we deliver and install across India — Mumbai, Pune, Bangalore, Delhi NCR, Hyderabad, Chennai, Ahmedabad, and all cities. Our logistics and installation teams ensure the same quality of service nationwide." },
              { q: "Can office furniture be customized to match our brand?", a: "Absolutely. We offer full customization — dimensions, finishes, laminate colours, handle styles, materials, and configurations. Our design team creates 3D layouts and detailed shop drawings before manufacturing begins, ensuring the furniture matches your brand and workspace perfectly." },
              { q: "Is Ezeefit a direct manufacturer or a dealer?", a: "Ezeefit is a direct manufacturer with our own factory in Bhiwandi, Thane. We design, manufacture, and install — eliminating middlemen. This means you get factory-direct pricing, consistent quality control, and the ability to fully customize every product." },
              { q: "What industries does Ezeefit supply office furniture to?", a: "We supply to 13+ industries including IT & Technology, Corporate Headquarters, Co-working Spaces, BPO & Call Centres, Startups, Public Sector, Banking & BFSI, Global Capability Centres (GCCs), FinTech, Logistics, Pharma, Data Centres, and Education institutions." },
            ].map((faq, i) => (
              <div key={i} className="border-b border-gray-200 pb-6">
                <h3 className="font-semibold text-[#0F172A] mb-2">{faq.q}</h3>
                <p className="text-sm text-[#64748B] leading-relaxed">{faq.a}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 bg-[#0F172A]">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24 text-center">
          <h2 className="text-2xl md:text-3xl font-bold text-white mb-4">Ready to Furnish Your Office in Mumbai?</h2>
          <p className="text-[#94A3B8] mb-8 max-w-2xl mx-auto leading-relaxed">
            Get a free consultation, site visit, and detailed quotation for your office furniture project. Call us at <a href="tel:+919820596811" className="phone-flash">9820596811</a> / <a href="tel:+919820042100" className="text-[#F97316] font-semibold">9820042100</a> or fill out the form below.
          </p>
          <div className="flex flex-wrap gap-4 justify-center">
            <Link to="/contact">
              <Button className="bg-[#F97316] hover:bg-[#EA580C] text-white px-8 py-6 text-base font-semibold">
                Request Free Quote <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
            </Link>
            <Link to="/case-studies">
              <Button variant="outline" className="border-white/30 text-white hover:bg-white/10 px-8 py-6 text-base">
                View Our Projects
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
    </>
  );
}
